import React, { useState, useEffect, useMemo } from 'react';
import { X, Check, ShoppingBag, Minus, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

export default function ProductQuickView({ product, onClose }) {
  const initialColor = product?.colors?.[0] || '';
  const [color, setColor] = useState(initialColor);
  const [size, setSize] = useState('36');
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  // Resolve the image for the selected color (fallback to base if no variant)
  const activeImage = useMemo(() => {
    const v = product?.variants?.find((x) => x.color === color);
    return v?.image || product?.image;
  }, [color, product]);

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!product) return null;

  const productForCart = { ...product, image: activeImage };

  const handleAdd = () => {
    addItem(productForCart, color, size, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  const handleBuyNow = () => {
    addItem(productForCart, color, size, qty);
    navigate('/sepet');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-0 sm:p-4 bg-stone-950/60 backdrop-blur-sm">
      <div className="relative bg-[#f7f2ec] w-full max-w-5xl max-h-[100vh] sm:max-h-[95vh] h-full sm:h-auto overflow-y-auto grid md:grid-cols-2">
        <button
          onClick={onClose}
          className="fixed sm:absolute top-3 right-3 z-20 h-10 w-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center text-stone-800 shadow-md"
          aria-label="Kapat"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Image side — capped on mobile so info stays visible */}
        <div className="bg-stone-100 relative overflow-hidden h-[46vh] sm:h-auto md:aspect-auto">
          <img
            key={activeImage}
            src={activeImage}
            alt={`${product.name} - ${color}`}
            className="w-full h-full object-cover animate-fadeIn"
          />
        </div>

        {/* Info side */}
        <div className="p-6 lg:p-9 flex flex-col">
          <span className="text-[10px] tracking-[0.28em] uppercase text-stone-500">{product.category}</span>
          <h3 className="mt-2 font-serif text-3xl lg:text-4xl text-stone-900 leading-tight">{product.name}</h3>
          <p className="mt-3 font-serif text-2xl text-stone-800">₺{product.price.toLocaleString('tr-TR')}</p>

          {/* Size grid */}
          <div className="mt-6">
            <p className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Beden</p>
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-2">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSize(s)}
                  className={`h-10 text-[13px] border transition-colors ${
                    size === s
                      ? 'bg-[#6b3520] text-[#f7f2ec] border-[#6b3520]'
                      : 'bg-white/60 border-stone-300 text-stone-800 hover:border-[#6b3520]'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Color rectangular buttons */}
          <div className="mt-6">
            <p className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Renk</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {product.colors.map((c) => {
                const active = color === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`px-4 py-2 text-[13px] border transition-colors ${
                      active
                        ? 'bg-[#6b3520] text-[#f7f2ec] border-[#6b3520]'
                        : 'bg-white/60 border-stone-300 text-stone-800 hover:border-[#6b3520]'
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quantity */}
          <div className="mt-6">
            <p className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Adet</p>
            <div className="mt-3 inline-flex items-center border border-stone-300 bg-white/60">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="h-10 w-10 flex items-center justify-center hover:bg-stone-100 text-stone-800"
                aria-label="Azalt"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-12 text-center text-[15px]">{qty}</span>
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(99, q + 1))}
                className="h-10 w-10 flex items-center justify-center hover:bg-stone-100 text-stone-800"
                aria-label="Artır"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-auto pt-8 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleAdd}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#6b3520] text-[#6b3520] text-[11px] tracking-[0.24em] uppercase hover:bg-[#6b3520] hover:text-[#f7f2ec] transition-colors"
            >
              {added ? <><Check className="h-4 w-4" /> Eklendi</> : <><ShoppingBag className="h-4 w-4" /> Sepete Ekle</>}
            </button>
            <button
              type="button"
              onClick={handleBuyNow}
              className="flex-1 px-6 py-3.5 bg-[#6b3520] text-[#f7f2ec] text-[11px] tracking-[0.24em] uppercase hover:bg-[#7a4227] transition-colors"
            >
              Sipariş Ver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
